/* utils related to searching site content. */

import { Content, ContentWithConfig } from '@/types/custom';

/*
 * Simple search returning score of content match with query.
 * 0: no match
 * > 0: match (higher is better)
 */
export const rankContent = (
  query: string,
  item: Content | ContentWithConfig,
): number => {
  const isDeep = Object.prototype.hasOwnProperty.call(item, 'configs');
  const content: Content = isDeep
    ? (item as ContentWithConfig).content
    : (item as Content);

  let score = 0;
  if (content.title.toLowerCase().includes(query)) {
    score += 2;
  }

  if (content.description?.toLowerCase().includes(query)) {
    score += 0.9;
  }
  if (content.body?.toLowerCase().includes(query)) {
    score += 0.5;
  }

  if (content.content_type.toLowerCase().includes(query)) {
    score += 0.1;
  }

  if (!isDeep) return score;

  const keywords = (item as ContentWithConfig).keywords;
  let keywordScore = keywords.reduce((acc, k) => {
    if (k.name.toLowerCase() === query) {
      return acc + 1.0;
    }

    // look for partial match (split on space or "-")
    const kStrs = k.name.toLowerCase().split(/[\s-]+/);
    if (kStrs.some((kStr) => kStr.includes(query))) {
      return acc + 0.5;
    }
    return acc;
  }, 0.0);

  keywordScore = Math.min(keywordScore, 1.5);
  return score + keywordScore;
};

/**
 * Return an index:score map of the content items that match the query.
 * The map will be sorted by score (high to low)
 * Items with search scores <= scoreThreshold will be excluded.
 *
 * TODO: support keywords search
 * TODO: refactor to support Content[] | ContentWithConfig[] (e.g. for searching transcript)
 */
export const searchContentList = (
  query: string,
  data: Content[] | ContentWithConfig[],
  scoreThreshold: number = 0.0,
): Record<number, number> => {
  if (data.length === 0) return {};

  // TODO: consider spliting on space and searching each word separately
  query = query.toLowerCase().trim();

  const indexScoreMap: Record<number, number> = {};
  for (let i = 0; i < data.length; i++) {
    const score = rankContent(query, data[i]);
    if (score > scoreThreshold) {
      indexScoreMap[i] = score;
    }
  }

  // Sort the map by score (high to low)
  return Object.fromEntries(
    Object.entries(indexScoreMap).sort((a, b) => b[1] - a[1]),
  );
};
